<script>
  import { onMount, onDestroy } from "svelte";
  import { fetchActivoDrops } from '../controllers/activoDropController.js';
  import AddAssetAssignmentModal from '../components/AddAssetAssignmentModal.svelte';
  import { toasts } from 'svelte-toasts';
  import { GOOGLE_MAPS_API_KEY, POCKETBASE_URL } from '../config.js';
  import pb from '../lib/pocketbase';
  import { t } from 'svelte-i18n';

  let isLoading = true;
  let error = null;
  let mapContainer;
  let selectedAssignment = null;
  let assignments = [];
  let totalAssignments = 0;
  let isAssignmentModalOpen = false;
  let mapInstance = null;
  let googleMapsLoaded = false;
  let editingLocation = null; // Track which assignment is being edited
  let showList = true; // Toggle between map and list view
  let locationEditMap = null; // Map instance for location editing modal
  let locationEditMarker = null; // Marker for location editing modal
  let locationEditMapContainer = null; // Container for location edit map
  let mapContainerRef = null; // Keep a reference to the map container

  // Filter states
  let statusFilter = 'all';
  let searchQuery = '';
  let activoSearch = '';
  let userSearch = '';

  // Flip state per assignment card and gallery caches
  let flippedCards = new Set();
  let activoGalleryCache = new Map();
  let activoGalleryLoading = new Set();

  // Atributos UI state and caches
  let atributosList = [];
  let shownAtributoIds = new Set();
  let activoAtributosCache = new Map();

  async function loadAtributosList() {
    try {
      const resp = await pb.collection('atributo').getList(1, 200, {
        filter: 'tipo = "activo"',
        sort: 'descrip',
        $autoCancel: false
      });
      atributosList = resp?.items || [];
    } catch (err) {
      console.warn('No se pudo cargar atributos:', err);
      atributosList = [];
    }
  }

  async function toggleAtributoShown(atributoId) {
    const isActive = shownAtributoIds.has(atributoId);
    if (isActive) {
      const next = new Set(shownAtributoIds);
      next.delete(atributoId);
      shownAtributoIds = next;
      return;
    }

    // Pre-cargar atributos para los activos actualmente visibles antes de mostrar
    const uniqueActivoIds = Array.from(new Set((filteredAssignments || []).map(a => a.expand?.activo?.id).filter(Boolean)));
    await Promise.all(uniqueActivoIds.map(id => ensureActivoAtributosLoaded(id)));

    const next = new Set(shownAtributoIds);
    next.add(atributoId);
    shownAtributoIds = next;
  }

  async function ensureActivoAtributosLoaded(activoId) {
    if (!activoId || activoAtributosCache.has(activoId)) return;
    try {
      const resp = await pb.collection('atributo_activo').getList(1, 50, {
        filter: `activo="${activoId}"`,
        expand: 'atributo',
        $autoCancel: false
      });
      const items = (resp?.items || []).map(item => ({
        atributo: item.expand?.atributo || item.atributo,
        valor: item.valor
      }));
      activoAtributosCache.set(activoId, items);
      activoAtributosCache = new Map(activoAtributosCache);
    } catch (err) {
      console.warn('No se pudo cargar atributos del activo:', err);
      activoAtributosCache.set(activoId, []);
      activoAtributosCache = new Map(activoAtributosCache);
    }
  }

  function getActivoAtributoValue(activoId, atributoId) {
    const list = activoAtributosCache.get(activoId) || [];
    const entry = list.find(it => (it.atributo?.id || it.atributo) === atributoId);
    return entry?.valor ?? '';
  }

  function buildFileUrl(record, fileName, thumbSize = '200x200') {
    if (!record || !fileName) return '';
    // First try PocketBase helper (adds token automatically when needed)
    try {
      const helperUrl = pb.files.getUrl(record, fileName, thumbSize ? { thumb: thumbSize } : undefined);
      if (helperUrl) return helperUrl;
    } catch (_) {}
    // Fallback to manual URL (append token if available)
    const base = (POCKETBASE_URL || '').replace(/\/$/, '');
    const collection = record.collectionId || record.collectionName;
    if (!collection || !record.id) return '';
    const params = [];
    if (thumbSize) params.push(`thumb=${encodeURIComponent(thumbSize)}`);
    const token = pb && pb.authStore && pb.authStore.token ? pb.authStore.token : '';
    if (token) params.push(`token=${encodeURIComponent(token)}`);
    const query = params.length ? `?${params.join('&')}` : '';
    return `${base}/api/files/${collection}/${record.id}/${fileName}${query}`;
  }

  function toggleCardFlip(assignment) {
    const id = assignment?.id;
    if (!id) return;
    const next = new Set(flippedCards);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
      const activoId = assignment?.expand?.activo?.id;
      if (activoId && !activoGalleryCache.has(activoId) && !activoGalleryLoading.has(activoId)) {
        loadActivoGallery(activoId).catch(() => {});
      }
    }
    flippedCards = next;
  }

  async function loadActivoGallery(activoId) {
    try {
      activoGalleryLoading.add(activoId);
      activoGalleryLoading = new Set(activoGalleryLoading);
      const resp = await pb.collection('activo_gallery').getList(1, 20, {
        filter: `activo = "${activoId}"`,
        sort: '-created',
        $autoCancel: false
      });
      const items = resp?.items || [];
      const urls = items.map(rec => buildFileUrl(rec, rec?.url)).filter(Boolean);
      activoGalleryCache.set(activoId, urls);
      activoGalleryCache = new Map(activoGalleryCache);
    } catch (err) {
      console.warn('No se pudo cargar activo_gallery:', err);
      activoGalleryCache.set(activoId, []);
      activoGalleryCache = new Map(activoGalleryCache);
    } finally {
      activoGalleryLoading.delete(activoId);
      activoGalleryLoading = new Set(activoGalleryLoading);
    }
  }

  // Pagination
  let currentPage = 1;
  let perPage = 100;

  // Status options for the new activo_drop structure
  const statusOptions = [
    { value: 'all', label: $t('assetMap.statusOptions.all') },
    { value: 'CONFIRMED', label: $t('assetMap.statusOptions.confirmed') },
    { value: 'PENDING', label: $t('assetMap.statusOptions.pending') },
    { value: 'RETURNED', label: $t('assetMap.statusOptions.returned') }
  ];

  // Status display mapping
  const statusDisplay = {
    'CONFIRMED': { label: $t('assetMap.statusDisplay.confirmed'), class: 'status-confirmed' },
    'PENDING': { label: $t('assetMap.statusDisplay.pending'), class: 'status-pending' },
    'RETURNED': { label: $t('assetMap.statusDisplay.returned'), class: 'status-returned' }
  };

  // Marker colors by status
  const statusColors = {
    'CONFIRMED': '#10b981', // green
    'PENDING': '#f59e0b',   // amber
    'RETURNED': '#ef4444'   // red
  };

  onMount(async () => {
    await loadAssignments();
    loadGoogleMaps();
    subscribeToActivoDropRealtime();
    loadAtributosList();
  });

  onDestroy(() => {
    try {
      if (activoDropSubscriptionRef) {
        pb.collection('activo_drop').unsubscribe(activoDropSubscriptionRef);
      } else {
        pb.collection('activo_drop').unsubscribe('*');
      }
    } catch (err) {
      console.warn('Error al desuscribir activo_drop realtime:', err);
    }
  });

  let activoDropSubscriptionRef = null;

  async function subscribeToActivoDropRealtime() {
    try {
      // Ensure any previous subscription is removed
      if (activoDropSubscriptionRef) {
        await pb.collection('activo_drop').unsubscribe(activoDropSubscriptionRef);
        activoDropSubscriptionRef = null;
      }

      activoDropSubscriptionRef = await pb.collection('activo_drop').subscribe('*', async (e) => {
        try {
          if (e.action === 'create' || e.action === 'update') {
            // Fetch expanded record to keep UI consistent
            const fullRecord = await pb.collection('activo_drop').getOne(e.record.id, { expand: 'user,activo' });
            const idx = assignments.findIndex(a => a.id === fullRecord.id);
            if (idx >= 0) {
              assignments[idx] = fullRecord;
              assignments = [...assignments];
            } else {
              assignments = [fullRecord, ...assignments];
            }
          } else if (e.action === 'delete') {
            assignments = assignments.filter(a => a.id !== e.record.id);
          }

          // Update legend and map markers if needed
          setTimeout(() => {
            updateLegendCounts();
            refreshMapMarkers();
          }, 50);
        } catch (err) {
          console.warn('Error handling activo_drop realtime event:', err);
          // Fallback: reload the page of assignments
          await loadAssignments();
          refreshMapMarkers();
        }
      });
    } catch (err) {
      console.error('No se pudo suscribir a activo_drop en tiempo real:', err);
    }
  }

  async function loadAssignments() {
    try {
      isLoading = true;
      const assignmentsResponse = await fetchActivoDrops(currentPage, perPage);
      
      if (assignmentsResponse) {
        assignments = assignmentsResponse.items || [];
        totalAssignments = assignmentsResponse.totalItems || 0;
      }
    } catch (err) {
      console.error('Error loading assignments:', err);
      error = 'Error al cargar las asignaciones';
    } finally {
      isLoading = false;
    }
  }

  function loadGoogleMaps() {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      googleMapsLoaded = true;
      initializeMap();
      return;
    }

    // Load Google Maps API
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
    script.onload = () => {
      googleMapsLoaded = true;
      initializeMap();
    };
    script.onerror = () => {
      console.error('Failed to load Google Maps API');
      error = 'Error al cargar Google Maps. Asegúrate de tener una API key válida.';
    };
    document.head.appendChild(script);
  }

  let mapInitRetryCount = 0;

  function initializeMap() {
    console.log('initializeMap called, mapContainer:', mapContainer, 'googleMapsLoaded:', googleMapsLoaded);
    
    // Try to ensure map container is bound
    if (!mapContainer) {
      mapContainer = ensureMapContainer();
    }
    
    if (!mapContainer || !googleMapsLoaded) {
      console.log('Map initialization failed - missing container or Google Maps not loaded');
      if (mapInitRetryCount < 20) {
        mapInitRetryCount += 1;
        setTimeout(() => initializeMap(), 50);
      }
      return;
    }
    mapInitRetryCount = 0;
    console.log('Creating map interface...');
    createMapInterface();
  }

  // Ensure map container is visible and map is ready before running a callback (e.g., center on item)
  function ensureMapReady(callback) {
    const tryPrepare = (retries = 10) => {
      setTimeout(() => {
        const currentMapContainer = document.querySelector('.map-container');
        if (currentMapContainer) {
          mapContainer = currentMapContainer;
          // If map instance is missing or detached, re-initialize
          const mapDiv = mapInstance?.getDiv?.();
          const detached = !mapDiv || !mapDiv.isConnected;
          if (!mapInstance || detached) {
            initializeMap();
            // Give the map a brief moment to initialize
            setTimeout(() => callback && callback(), 120);
          } else {
            callback && callback();
          }
        } else if (retries > 0) {
          tryPrepare(retries - 1);
        }
      }, 50);
    };
    tryPrepare();
  }

  function createMapInterface() {
    console.log('createMapInterface called, mapContainer:', mapContainer);
    // Clear previous content
    mapContainer.innerHTML = '';

    // Create the map container
    const mapDiv = document.createElement('div');
    mapDiv.id = 'asset-map';
    mapDiv.style.cssText = `
      width: 100%;
      height: 600px;
      border-radius: 8px;
      overflow: hidden;
      position: relative;
    `;

    mapContainer.appendChild(mapDiv);
    console.log('Map div created and added to container');

    // Initialize the map
    initializeGoogleMap(mapDiv);
    
    // Add legend overlay
    addMapLegend();
  }

  function initializeGoogleMap(mapDiv) {
    // Create map centered on Guatemala
    const map = new google.maps.Map(mapDiv, {
      center: { lat: 14.6349, lng: -90.5069 },
      zoom: 10,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });
    
    // Add assignment markers
    addAssignmentMarkers(map);
    
    // Store map reference for later use
    window.assetMap = map;
    mapInstance = map;
    
    console.log('Google Map initialized, mapInstance set to:', mapInstance);
  }

  function addAssignmentMarkers(map) {
    if (filteredAssignments.length === 0) return;

    filteredAssignments.forEach((assignment) => {
      try {
        // Resolve location data (supports object or legacy JSON string)
        let location = null;
        if (assignment.location) {
          location = typeof assignment.location === 'string'
            ? (() => { try { return JSON.parse(assignment.location); } catch { return null; } })()
            : assignment.location;
        }

        if (!location || !location.lat || !location.lng) {
          console.warn('Missing location coordinates for assignment:', assignment.id);
          return;
        }

        // Create assignment marker
        const marker = new google.maps.Marker({
          position: { lat: location.lat, lng: location.lng },
          map: map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: statusColors[assignment.sta] || '#8b5cf6',
            fillOpacity: 1,
            strokeColor: 'white',
            strokeWeight: 2
          },
          title: 'Asignación de Activo'
        });

        // Create info window content
        const infoWindowContent = `
          <div style="min-width: 200px;">
            <h4 style="margin: 0 0 8px 0; color: #1e293b;">Asignación de Activo</h4>
            <p style="margin: 0 0 8px 0; color: #64748b; font-size: 14px;">
              <strong>Usuario:</strong> ${assignment.expand?.user?.name || 'N/A'}
            </p>
            <p style="margin: 0 0 8px 0; color: #64748b; font-size: 14px;">
              <strong>Activo:</strong> ${assignment.expand?.activo?.nombre || 'N/A'}
            </p>
            <p style="margin: 0 0 8px 0; color: #64748b; font-size: 14px;">
              <strong>Estado:</strong> ${assignment.sta || 'PENDIENTE'}
            </p>
            <p style="margin: 0 0 8px 0; color: #64748b; font-size: 14px;">
              <strong>Fecha de Retorno:</strong> ${assignment.return ? new Date(assignment.return).toLocaleDateString('es-GT') : 'No especificada'}
            </p>
            <p style="margin: 0 0 8px 0; color: #64748b; font-size: 14px;">
              <strong>Asignado:</strong> ${new Date(assignment.created).toLocaleDateString('es-GT')}
            </p>
            <div style="margin-top: 8px; text-align: center;">
              <button id="view-details-${assignment.id}" data-assignment-id="${assignment.id}" style="background: #8b5cf6; color: white; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 12px;">
                Ver Detalles
              </button>
            </div>
          </div>
        `;

        const infoWindow = new google.maps.InfoWindow({
          content: infoWindowContent
        });

        // Add click event to marker
        marker.addListener('click', () => {
          infoWindow.open(map, marker);
          // Select the assignment immediately when marker is clicked
          selectAssignment(assignment);
          // Defer DOM query until content is attached
          setTimeout(() => {
            try {
              const btn = document.getElementById(`view-details-${assignment.id}`);
              if (btn) {
                btn.onclick = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Switch to list view and apply filters to show only this asset
                  showList = true;
                  // Clear other filters
                  statusFilter = 'all';
                  userSearch = '';
                  searchQuery = '';
                  // Filter by activo name or code
                  const nombre = assignment.expand?.activo?.nombre || '';
                  const codigo = assignment.expand?.activo?.codigo || '';
                  // Prefer code, fallback to name
                  activoSearch = codigo || nombre;
                  // Close info window
                  infoWindow.close();
                  // Ensure scroll to list
                  const listHeader = document.querySelector('.assignments-list');
                  if (listHeader && typeof listHeader.scrollIntoView === 'function') {
                    listHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                };
              }
            } catch (err) {
              console.warn('Could not bind view details button:', err);
            }
          }, 50);
        });

      } catch (error) {
        console.error('Error creating assignment marker:', error);
      }
    });
  }

  function addMapLegend() {
    const legend = document.createElement('div');
    legend.className = 'map-legend-overlay';
    legend.innerHTML = `
      <div class="legend-item">
        <span class="legend-color" style="background: ${statusColors['PENDING']}"></span>
        <span>${$t('assetMap.legend.pending')} (<span id="pending-count">0</span>)</span>
      </div>
      <div class="legend-item">
        <span class="legend-color" style="background: ${statusColors['CONFIRMED']}"></span>
        <span>${$t('assetMap.legend.confirmed')} (<span id="confirmed-count">0</span>)</span>
      </div>
      <div class="legend-item">
        <span class="legend-color" style="background: ${statusColors['RETURNED']}"></span>
        <span>${$t('assetMap.legend.returned')} (<span id="returned-count">0</span>)</span>
      </div>
    `;

    mapContainer.appendChild(legend);
    
    // Update the counts
    updateLegendCounts();
  }

  function refreshMapMarkers() {
    if (mapInstance && !showList) {
      // Clear existing markers by recreating the map
      initializeMap();
    }
  }

  function updateLegendCounts() {
    if (mapContainer) {
      const pending = filteredAssignments.filter(a => a.sta === 'PENDING').length;
      const confirmed = filteredAssignments.filter(a => a.sta === 'CONFIRMED').length;
      const returned = filteredAssignments.filter(a => a.sta === 'RETURNED').length;

      const pendingEl = mapContainer.querySelector('#pending-count');
      const confirmedEl = mapContainer.querySelector('#confirmed-count');
      const returnedEl = mapContainer.querySelector('#returned-count');

      if (pendingEl) pendingEl.textContent = pending;
      if (confirmedEl) confirmedEl.textContent = confirmed;
      if (returnedEl) returnedEl.textContent = returned;
    }
  }

  function clearFilters() {
    statusFilter = 'all';
    searchQuery = '';
    activoSearch = '';
    userSearch = '';
    loadAssignments();
  }

  // Remove server reload on filter change; filtering is client-side

  // Filter assignments based on current filters
  $: filteredAssignments = assignments.filter(assignment => {
    let matchesStatus = true;
    let matchesSearch = true;
    let matchesActivo = true;
    let matchesUser = true;

    // Status filter
    if (statusFilter === 'CONFIRMED') {
      matchesStatus = assignment.sta === 'CONFIRMED';
    } else if (statusFilter === 'PENDING') {
      matchesStatus = assignment.sta === 'PENDING';
    } else if (statusFilter === 'RETURNED') {
      matchesStatus = assignment.sta === 'RETURNED';
    }

    // Activo search filter (codigo or nombre)
    if (activoSearch) {
      const q = activoSearch.toLowerCase();
      const assetName = assignment.expand?.activo?.nombre?.toLowerCase() || '';
      const assetCode = assignment.expand?.activo?.codigo?.toLowerCase() || '';
      matchesActivo = assetName.includes(q) || assetCode.includes(q);
    }

    // User search filter (name, lastName, email)
    if (userSearch) {
      const q = userSearch.toLowerCase();
      const userName = assignment.expand?.user?.name?.toLowerCase() || '';
      const userLastName = assignment.expand?.user?.lastName?.toLowerCase() || '';
      const userEmail = assignment.expand?.user?.email?.toLowerCase() || '';
      matchesUser = userName.includes(q) || userLastName.includes(q) || userEmail.includes(q);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const userName = assignment.expand?.user?.name?.toLowerCase() || '';
      const assetName = assignment.expand?.activo?.nombre?.toLowerCase() || '';
      const assetCode = assignment.expand?.activo?.codigo?.toLowerCase() || '';
      
      matchesSearch = userName.includes(query) || 
                     assetName.includes(query) || 
                     assetCode.includes(query);
    }

    return matchesStatus && matchesSearch && matchesActivo && matchesUser;
  });

  // Watch for data or filter changes and update legend/map
  $: if (!isLoading) {
    // Ensure reactivity on filters by referencing filteredAssignments
    const _filteredCount = filteredAssignments.length;
    setTimeout(() => {
      updateLegendCounts();
      refreshMapMarkers();
    }, 50);
  }

  // Ensure atributo values are loaded for visible assets when any atributo is shown
  $: if (shownAtributoIds.size > 0 && filteredAssignments) {
    const ids = Array.from(new Set(filteredAssignments.map(a => a.expand?.activo?.id).filter(Boolean)));
    ids.forEach(id => ensureActivoAtributosLoaded(id));
  }

  // Watch for selectedAssignment changes to ensure UI updates
  $: if (selectedAssignment) {
    console.log('selectedAssignment changed:', selectedAssignment);
  }

  // Function to select an assignment
  function selectAssignment(assignment) {
    if (assignment) {
      // Force reactivity by creating a new object reference
      selectedAssignment = assignment;
      console.log('Assignment selected:', selectedAssignment);
      
      // If we're in map view, center the map on the selected assignment
      if (!showList && mapInstance && assignment.location) {
        const location = typeof assignment.location === 'string'
          ? (() => { try { return JSON.parse(assignment.location); } catch { return null; } })()
          : assignment.location;
        if (location?.lat && location?.lng) {
          mapInstance.setCenter({ lat: location.lat, lng: location.lng });
          mapInstance.setZoom(15);
        }
      }
    }
  }

  // Function to ensure map container is properly bound
  function ensureMapContainer() {
    if (!mapContainer) {
      const currentMapContainer = document.querySelector('.map-container');
      if (currentMapContainer) {
        mapContainer = currentMapContainer;
        console.log('Map container re-bound:', mapContainer);
      }
    }
    return mapContainer;
  }

  // Expose selectAssignment function globally for onclick events
  window.selectAssignment = (assignmentId) => {
    const assignment = assignments.find(assignment => assignment.id === assignmentId);
    if (assignment) {
      selectedAssignment = assignment;
      console.log('Assignment selected via global function:', selectedAssignment);
    }
  };

  // Handle assignment modal
  function openAssignmentModal() {
    console.log('Opening assignment modal, current state:', isAssignmentModalOpen);
    if (isAssignmentModalOpen) {
      console.log('Modal already open, ignoring duplicate open request');
      return;
    }
    isAssignmentModalOpen = true;
    console.log('Modal opened successfully');
  }

  function closeAssignmentModal() {
    isAssignmentModalOpen = false;
  }

  function handleAssignmentCreated(assignment) {
    // Refresh the map to show the new assignment
    console.log('New assignment created, refreshing map:', assignment);
    
    // Close the modal if it's still open
    if (isAssignmentModalOpen) {
      isAssignmentModalOpen = false;
    }
    
    // Reload data to include the new assignment
    loadAssignments().then(() => {
      console.log('Data reloaded, refreshing map markers...');
      
      // Refresh the map to show new assignment
      if (mapInstance) {
        initializeMap();
      }
    }).catch(error => {
      console.error('Error reloading data after assignment creation:', error);
    });
  }

  // Location editing functions
  function startLocationEdit(assignment) {
    const resolvedLoc = assignment.location
      ? (typeof assignment.location === 'string'
          ? (() => { try { return JSON.parse(assignment.location); } catch { return {}; } })()
          : assignment.location)
      : {};
    editingLocation = {
      id: assignment.id,
      currentLat: resolvedLoc.lat ?? null,
      currentLng: resolvedLoc.lng ?? null,
      newLat: resolvedLoc.lat ?? null,
      newLng: resolvedLoc.lng ?? null
    };
    
    // Initialize the location edit map after the modal is rendered
    setTimeout(() => {
      initializeLocationEditMap();
    }, 100);
  }

  function cancelLocationEdit() {
    editingLocation = null;
    // Clean up map
    if (locationEditMap) {
      locationEditMap = null;
    }
    if (locationEditMarker) {
      locationEditMarker = null;
    }
  }

  function initializeLocationEditMap() {
    if (!locationEditMapContainer || !googleMapsLoaded) return;

    // Create the map
    const map = new google.maps.Map(locationEditMapContainer, {
      center: { 
        lat: editingLocation.currentLat || 14.6349, 
        lng: editingLocation.currentLng || -90.5069 
      },
      zoom: 13,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

    locationEditMap = map;

    // Add current location marker if it exists
    if (editingLocation.currentLat && editingLocation.currentLng) {
      locationEditMarker = new google.maps.Marker({
        position: { 
          lat: editingLocation.currentLat, 
          lng: editingLocation.currentLng 
        },
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#8b5cf6',
          fillOpacity: 1,
          strokeColor: 'white',
          strokeWeight: 2
        },
        title: 'Ubicación actual'
      });
    }

    // Add click listener to drop new pin
    map.addListener('click', (event) => {
      const position = event.latLng;
      
      // Remove existing marker
      if (locationEditMarker) {
        locationEditMarker.setMap(null);
      }

      // Create new marker
      locationEditMarker = new google.maps.Marker({
        position: position,
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#10b981',
          fillOpacity: 1,
          strokeColor: 'white',
          strokeWeight: 2
        },
        title: 'Nueva ubicación',
        draggable: true
      });

      // Update coordinates
      editingLocation.newLat = position.lat();
      editingLocation.newLng = position.lng();
      editingLocation = { ...editingLocation };

      // Add drag listener to update coordinates when marker is moved
      locationEditMarker.addListener('dragend', (event) => {
        const newPosition = event.latLng;
        editingLocation.newLat = newPosition.lat();
        editingLocation.newLng = newPosition.lng();
        editingLocation = { ...editingLocation };
      });
    });

    // Add instructions overlay
    addLocationEditInstructions(map);
  }

  function addLocationEditInstructions(map) {
    const instructions = document.createElement('div');
    instructions.className = 'location-edit-instructions';
    instructions.innerHTML = `
      <div class="instructions-content">
        <p><strong>Instrucciones:</strong></p>
        <p>• Haz clic en el mapa para colocar el pin</p>
        <p>• Arrastra el pin para ajustar la posición</p>
        <p>• Haz doble clic para hacer zoom</p>
      </div>
    `;

    map.controls[google.maps.ControlPosition.TOP_LEFT].push(instructions);
  }

  async function saveLocationChange() {
    if (!editingLocation || !editingLocation.newLat || !editingLocation.newLng) {
      toasts.error($t('assetMap.toasts.noLocationSelected'));
      return;
    }

    try {
      const newLocation = {
        lat: parseFloat(editingLocation.newLat),
        lng: parseFloat(editingLocation.newLng)
      };

      // Update the assignment in the database
      const { updateActivoDropAssignment } = await import('../controllers/activoDropController.js');
      const updatedAssignment = await updateActivoDropAssignment(editingLocation.id, {
        location: newLocation
      });

      if (updatedAssignment) {
        // Update the local assignment data
        const assignmentIndex = assignments.findIndex(a => a.id === editingLocation.id);
        if (assignmentIndex !== -1) {
          assignments[assignmentIndex].location = newLocation;
          assignments = [...assignments]; // Trigger reactivity
        }

        // Refresh the map to show the new location
        if (mapInstance) {
          initializeMap();
        }

        toasts.success($t('assetMap.toasts.locationUpdated'));
        cancelLocationEdit();
      }
    } catch (error) {
      console.error('Error updating location:', error);
      toasts.error($t('assetMap.toasts.locationUpdateError'));
    }
  }

  function updateLocationCoordinates(field, value) {
    if (editingLocation) {
      editingLocation[field] = value;
      editingLocation = { ...editingLocation }; // Trigger reactivity
    }
  }

  function toggleView() {
    console.log('Toggling view from', showList ? 'list' : 'map', 'to', showList ? 'map' : 'list');
    showList = !showList;
    
    // If switching to map view, initialize the map
    if (!showList) {
      console.log('Switching to map view, initializing map...');
      toasts.info($t('assetMap.toasts.switchingToMap'));
      
      ensureMapReady(() => {
        if (!mapInstance) {
          initializeMap();
        } else {
          // Map already exists, just refresh markers
          addAssignmentMarkers(mapInstance);
        }
        toasts.success($t('assetMap.toasts.mapViewLoaded'));
      });
    } else {
      console.log('Switching to list view');
      toasts.info($t('assetMap.toasts.switchingToList'));
    }
  }


</script>

<svelte:head>
  <style>
    .map-interface {
      width: 100%;
      min-height: 600px;
      background: #f8fafc;
      border-radius: 12px;
      overflow: hidden;
    }

    .map-placeholder {
      padding: 0;
      height: 100%;
    }

    .map-content {
      padding: 24px;
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .map-header {
      text-align: center;
      margin-bottom: 32px;
    }

    .map-header h3 {
      font-size: 24px;
      font-weight: 700;
      color: #1e293b;
      margin: 0 0 8px 0;
    }

    .map-header p {
      color: #64748b;
      margin: 0;
      font-size: 16px;
    }

    .map-visualization {
      flex: 1;
      margin-bottom: 24px;
    }







    .filters-container {
      background: white;
      border-radius: 12px;
      padding: 24px;
      margin-bottom: 24px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      border: 1px solid #e2e8f0;
      position: sticky;
      top: 0;
      z-index: 5;
    }

    .filters-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 2px solid #f1f5f9;
    }

    .filters-title {
      font-size: 20px;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    /* Atributos filter styles */
    .atributos-filter-bar {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 8px;
    }
    .atributo-chip {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px;
      border: 1px solid #e2e8f0;
      border-radius: 999px;
      background: #f8fafc;
      color: #1f2937;
      font-size: 12px;
      cursor: pointer;
      transition: background 0.2s ease, border-color 0.2s ease;
      user-select: none;
    }
    .atributo-chip:hover {
      background: #eef2ff;
      border-color: #c7d2fe;
    }
    .atributo-chip.active {
      background: #3b82f6;
      border-color: #2563eb;
      color: white;
    }
    .atributo-check {
      width: 16px;
      height: 16px;
      border-radius: 4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: white;
      border: 2px solid #e2e8f0;
    }
    .atributo-chip.active .atributo-check {
      background: #22c55e;
      border-color: #16a34a;
      color: white;
    }

    .filters-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;
      align-items: end;
    }

    .filter-group {
      display: flex;
      flex-direction: column;
    }

    .filter-label {
      font-size: 12px;
      color: #64748b;
      font-weight: 600;
      text-transform: uppercase;
      margin-bottom: 6px;
      letter-spacing: 0.5px;
    }

    .filter-input,
    .filter-select {
      padding: 12px 16px;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      font-size: 14px;
      background: white;
      color: #1e293b;
      transition: all 0.2s ease;
      font-weight: 500;
      min-height: 48px;
    }

    .filter-input:focus,
    .filter-select:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .filter-select {
      cursor: pointer;
    }

    .filter-select:hover {
      border-color: #cbd5e1;
    }

    /* Enhanced dropdown styling */
    .filter-select {
      appearance: none;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
      background-position: right 12px center;
      background-repeat: no-repeat;
      background-size: 16px;
      padding-right: 40px;
    }

    .filter-select:focus {
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%233b82f6' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
    }

    .filter-select:disabled {
      background-color: #f8fafc;
      color: #94a3b8;
      cursor: not-allowed;
      opacity: 0.7;
    }

    .filter-select:disabled:hover {
      border-color: #e2e8f0;
    }

    .clear-filters-btn {
      padding: 10px 20px;
      background: #64748b;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .clear-filters-btn:hover {
      background: #475569;
      transform: translateY(-1px);
    }

    .asset-details {
      background: white;
      border-radius: 12px;
      padding: 24px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      margin-top: 24px;
    }

    .asset-details h3 {
      color: #1e293b;
      margin: 0 0 20px 0;
      font-size: 20px;
      font-weight: 600;
      border-bottom: 2px solid #e2e8f0;
      padding-bottom: 12px;
    }

    .asset-info {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 20px;
    }

    .info-item {
      display: flex;
      flex-direction: column;
    }

    .info-label {
      font-size: 12px;
      color: #64748b;
      font-weight: 600;
      text-transform: uppercase;
      margin-bottom: 6px;
      letter-spacing: 0.5px;
    }

    .info-value {
      font-size: 14px;
      color: #1e293b;
      font-weight: 500;
    }

    .status-badge {
      display: inline-block;
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .status-confirmed {
      background: #dcfce7;
      color: #166534;
    }

    .status-pending {
      background: #fef3c7;
      color: #92400e;
    }

    .status-returned {
      background: #fee2e2;
      color: #991b1b;
    }

    .loading-spinner {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 400px;
    }

    .spinner {
      width: 48px;
      height: 48px;
      border: 4px solid #e2e8f0;
      border-top: 4px solid #3b82f6;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .error-message {
      text-align: center;
      color: #ef4444;
      padding: 40px 20px;
      font-size: 16px;
    }

    .add-assignment-btn {
      background: #10b981;
      color: white;
      border: none;
      padding: 10px 16px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .add-assignment-btn:hover:not(:disabled) {
      background: #059669;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    .add-assignment-btn:disabled {
      background: #9ca3af;
      cursor: not-allowed;
      opacity: 0.6;
      transform: none;
      box-shadow: none;
    }

    .add-assignment-btn svg {
      width: 16px;
      height: 16px;
    }

    /* Google Maps specific styles */
    .map-legend-overlay {
      position: absolute;
      top: 10px;
      right: 10px;
      background: white;
      padding: 15px;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      z-index: 1000;
      font-size: 12px;
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    .legend-item:last-child {
      margin-bottom: 0;
    }

    .legend-color {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      display: inline-block;
    }

    .legend-color.assignment {
      background: #8b5cf6;
    }

    /* Toggle View Button */
    .toggle-view-btn {
      padding: 10px 16px;
      background: #6366f1;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .toggle-view-btn:hover {
      background: #4f46e5;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    /* View Status Indicator */
    .view-status {
      display: flex;
      align-items: center;
    }

    .view-indicator {
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 14px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      transition: all 0.2s ease;
    }

    .view-indicator.list-active {
      background: #3b82f6;
      color: white;
    }

    .view-indicator.map-active {
      background: #10b981;
      color: white;
    }

    /* Assignments List Styles */
    .assignments-list {
      padding: 20px 0;
    }

    .list-header {
      margin-bottom: 24px;
      text-align: center;
    }

    .list-title {
      font-size: 24px;
      font-weight: 700;
      color: #1e293b;
      margin: 0 0 8px 0;
    }

    .list-subtitle {
      color: #64748b;
      font-size: 16px;
      margin: 0;
    }

    .assignments-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
      gap: 20px;
      margin-bottom: 24px;
    }

    .assignment-card {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 20px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      transition: all 0.2s ease;
      perspective: 1000px;
      position: relative;
    }

    .assignment-card:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }

    /* Flip card styles */
    .flip-inner {
      position: relative;
      width: 100%;
      transform-style: preserve-3d;
      transition: transform 650ms cubic-bezier(0.22, 1, 0.36, 1);
      will-change: transform;
      min-height: 180px;
    }
    .assignment-card.is-flipped .flip-inner {
      transform: rotateY(180deg);
    }
    .flip-face {
      position: relative;
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;
    }
    .flip-back {
      position: absolute;
      inset: 0;
      transform: rotateY(180deg);
      display: flex;
      flex-direction: column;
    }
    .gallery-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
    }
    .gallery-item {
      width: 100%;
      aspect-ratio: 1 / 1;
      overflow: hidden;
      border-radius: 8px;
      background: #f1f5f9;
    }
    .gallery-item img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 200ms ease;
    }
    .gallery-item img:hover {
      transform: scale(1.05);
    }

    .assignment-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 16px;
    }

    .assignment-title h4 {
      font-size: 18px;
      font-weight: 600;
      color: #1e293b;
      margin: 0 0 4px 0;
    }

    .asset-code {
      font-size: 14px;
      color: #64748b;
      font-weight: 500;
    }

    .assignment-details {
      margin-bottom: 20px;
    }

    .detail-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      padding: 8px 0;
      border-bottom: 1px solid #f1f5f9;
    }

    .detail-item:last-child {
      border-bottom: none;
      margin-bottom: 0;
    }

    .detail-label {
      font-size: 14px;
      color: #64748b;
      font-weight: 500;
    }

    .detail-value {
      font-size: 14px;
      color: #1e293b;
      font-weight: 600;
    }

    .assignment-actions {
      display: flex;
      gap: 8px;
      justify-content: flex-start;
    }

    .edit-location-btn,
    .select-assignment-btn {
      flex: 0 0 auto;
      width: 40px;
      height: 40px;
      padding: 0;
      border: none;
      border-radius: 10px;
      font-size: 0; /* hide any text if present */
      cursor: pointer;
      transition: all 0.2s ease;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .edit-location-btn {
      background: #f59e0b;
      color: white;
    }

    .edit-location-btn:hover {
      background: #d97706;
      transform: translateY(-1px);
    }

    .select-assignment-btn {
      background: #3b82f6;
      color: white;
    }

    .select-assignment-btn:hover {
      background: #2563eb;
      transform: translateY(-1px);
    }

    .edit-location-btn svg,
    .select-assignment-btn svg {
      width: 18px;
      height: 18px;
    }

    .no-assignments {
      text-align: center;
      padding: 40px 20px;
      color: #64748b;
    }

    /* Location Edit Modal Styles */
    .location-edit-modal {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
    }

    .modal-content {
      background: white;
      border-radius: 12px;
      width: 90%;
      max-width: 800px;
      max-height: 90vh;
      overflow-y: auto;
      position: relative;
      z-index: 1001;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px 24px 16px 24px;
      border-bottom: 1px solid #e2e8f0;
    }

    .modal-header h3 {
      font-size: 20px;
      font-weight: 600;
      color: #1e293b;
      margin: 0;
    }

    .close-btn {
      background: none;
      border: none;
      color: #64748b;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.2s ease;
    }

    .close-btn:hover {
      background: #f1f5f9;
      color: #1e293b;
    }

    .modal-body {
      padding: 24px;
    }

    .location-map-container {
      margin-bottom: 20px;
    }

    .location-edit-map {
      width: 100%;
      height: 400px;
      border-radius: 8px;
      border: 2px solid #e2e8f0;
      overflow: hidden;
    }

    .location-info {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
    }

    .current-location,
    .new-location {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 16px;
    }

    .current-location p,
    .new-location p {
      margin: 0 0 8px 0;
      font-size: 14px;
      color: #374151;
    }

    .current-location p:last-child,
    .new-location p:last-child {
      margin-bottom: 0;
      font-family: monospace;
      background: white;
      padding: 8px 12px;
      border-radius: 4px;
      border: 1px solid #e2e8f0;
    }

    .location-edit-instructions {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 12px;
      margin: 10px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      font-size: 12px;
      color: #374151;
    }

    .instructions-content p {
      margin: 0 0 4px 0;
    }

    .instructions-content p:last-child {
      margin-bottom: 0;
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding: 16px 24px 24px 24px;
      border-top: 1px solid #e2e8f0;
    }

    .cancel-btn,
    .save-btn {
      padding: 12px 24px;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .cancel-btn {
      background: #f1f5f9;
      color: #374151;
    }

    .cancel-btn:hover {
      background: #e2e8f0;
    }

    .save-btn {
      background: #10b981;
      color: white;
    }

    .save-btn:hover {
      background: #059669;
    }

    @media (max-width: 768px) {
      .filters-grid {
        grid-template-columns: 1fr;
        gap: 12px;
      }
      
      .filter-select,
      .filter-input {
        min-height: 44px;
        font-size: 16px; /* Prevents zoom on iOS */
      }
      
      .asset-info {
        grid-template-columns: 1fr;
      }

      .assignments-grid {
        grid-template-columns: 1fr;
      }

      .location-info {
        grid-template-columns: 1fr;
      }

      .modal-content {
        width: 95%;
        margin: 20px;
      }

      .location-edit-map {
        height: 300px;
      }
    }

    @media (max-width: 480px) {
      .filters-grid {
        gap: 8px;
      }
      
      .filter-group {
        margin-bottom: 8px;
      }
    }
  </style>
</svelte:head>

<div class="w-full px-4">
  <!-- Filters Section -->
  <div class="filters-container">
    <div class="filters-header">
      <h3 class="filters-title">{$t('assetMap.filtersTitle')}</h3>
      <button class="clear-filters-btn" on:click={clearFilters}>
        <i class="fas fa-times"></i>
        {$t('assetMap.clearFilters')}
      </button>
    </div>
    
    <div class="filters-grid">
      <div class="filter-group">
        <label class="filter-label" for="filter-estado">{$t('assetMap.statusLabel')}</label>
        <select id="filter-estado" class="filter-select" bind:value={statusFilter}>
          {#each statusOptions as option}
            <option value={option.value}>{option.label}</option>
          {/each}
        </select>
      </div>
      
      <div class="filter-group">
        <label class="filter-label" for="filter-activo">{$t('assetMap.assetLabel')}</label>
        <input 
          type="text" 
          class="filter-input"
          id="filter-activo"
          placeholder={$t('assetMap.assetPlaceholder')}
          bind:value={activoSearch}
        />
      </div>
      
      <div class="filter-group">
        <label class="filter-label" for="filter-usuario">{$t('assetMap.userLabel')}</label>
        <input 
          type="text" 
          class="filter-input"
          id="filter-usuario"
          placeholder={$t('assetMap.userPlaceholder')}
          bind:value={userSearch}
        />
      </div>
      
      <div class="filter-group">
        <label class="filter-label" for="filter-general">{$t('assetMap.searchLabel')}</label>
        <input 
          type="text" 
          class="filter-input" 
          id="filter-general"
          placeholder={$t('assetMap.searchPlaceholder')}
          bind:value={searchQuery}
        />
      </div>
    </div>

    {#if atributosList.length > 0}
      <div class="mt-3">
        <div class="filter-label">Atributos</div>
        <div class="atributos-filter-bar">
          {#each atributosList as atr (atr.id)}
            <div 
              class="atributo-chip {shownAtributoIds.has(atr.id) ? 'active' : ''}"
              on:click={() => toggleAtributoShown(atr.id)}
              title={atr.descrip}
            >
              <span class="atributo-check">
                {#if shownAtributoIds.has(atr.id)}
                  ✓
                {/if}
              </span>
              <span>{atr.descrip}</span>
            </div>
          {/each}
        </div>
      </div>
    {/if}
  </div>

  <!-- Map Section -->
  <div class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
    <div class="rounded-t mb-0 px-4 py-3 bg-transparent">
      <div class="flex flex-wrap items-center">
        <div class="relative w-full max-w-full flex-grow flex-1">
          <h6 class="uppercase text-blueGray-400 mb-1 text-xs font-semibold">
            {$t('assetMap.headerSmall')}
          </h6>
          <h2 class="text-blueGray-700 text-xl font-semibold">
            {$t('assetMap.headerTitle')}
          </h2>
          <p class="text-blueGray-400 text-sm mt-1">
            {$t('assetMap.totalAssignments')}: {assignments.length} | {$t('assetMap.showing')}: {filteredAssignments.length}
            {#if selectedAssignment}
              | {$t('assetMap.selected')}: {selectedAssignment.expand?.activo?.nombre || 'N/A'}
            {/if}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <div class="view-status">
            <span class="view-indicator {showList ? 'list-active' : 'map-active'}">
              {showList ? `📋 ${$t('assetMap.viewList')}` : `🗺️ ${$t('assetMap.viewMap')}`}
            </span>
          </div>
          <button 
            class="toggle-view-btn"
            on:click={toggleView}
            title={$t('assetMap.toggleTooltip')}
          >
            {showList ? $t('assetMap.viewOnMap') : $t('assetMap.viewList')}
          </button>
          <button 
            id="new-assignment-btn"
            class="add-assignment-btn"
            on:click|preventDefault|stopPropagation={(e) => {
              console.log('Button clicked, event:', e);
              openAssignmentModal();
            }}
            title={$t('assetMap.newAssignment')}
            disabled={isAssignmentModalOpen}
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            {$t('assetMap.newAssignment')}
          </button>
        </div>
      </div>
    </div>
    
    <div class="p-4 flex-auto">
      {#if isLoading}
        <div class="loading-spinner">
          <div class="spinner"></div>
        </div>
      {:else if error}
        <div class="error-message">
          {error}
        </div>
      {:else if showList}
        <!-- List View -->
        <div class="assignments-list">
          <div class="list-header">
            <h3 class="list-title">{$t('assetMap.listTitle')}</h3>
            <p class="list-subtitle">{$t('assetMap.listSubtitle')}</p>
          </div>
          
          <div class="assignments-grid">
            {#each filteredAssignments as assignment (assignment.id)}
              <div class="assignment-card {flippedCards.has(assignment.id) ? 'is-flipped' : ''}" on:click={() => toggleCardFlip(assignment)}>
                <div class="flip-inner">
                  <div class="flip-face flip-front">
                    <div class="assignment-header">
                      <div class="assignment-title">
                        <h4>{assignment.expand?.activo?.nombre || $t('assetMap.noName')}</h4>
                        <span class="asset-code">{assignment.expand?.activo?.codigo || $t('assetMap.noCode')}</span>
                      </div>
                      <span class="status-badge {statusDisplay[assignment.sta]?.class || 'status-pending'}">
                        {statusDisplay[assignment.sta]?.label || $t('assetMap.statusPending')}
                      </span>
                    </div>
                    
                    <div class="assignment-details">
                      <div class="detail-item">
                        <span class="detail-label">{$t('assetMap.infoWindow.user')}:</span>
                        <span class="detail-value">{assignment.expand?.user?.name || $t('assetMap.noName')}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">{$t('assetMap.infoWindow.assigned')}:</span>
                        <span class="detail-value">{new Date(assignment.created).toLocaleDateString('es-GT')}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">{$t('assetMap.infoWindow.returnDate')}:</span>
                        <span class="detail-value">{assignment.return ? new Date(assignment.return).toLocaleDateString('es-GT') : $t('assetMap.noReturnDate')}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Ubicación:</span>
                        <span class="detail-value">
                          {#if assignment.location}
                            {#if typeof assignment.location === 'string'}
                              {#key assignment.location}
                                {(() => {
                                  try {
                                    const loc = JSON.parse(assignment.location);
                                    return loc?.lat && loc?.lng ? `${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}` : $t('assetMap.noLocationDefined')
                                  } catch (e) {
                                    return $t('assetMap.noLocationDefined');
                                  }
                                })()}
                              {/key}
                            {:else}
                              {assignment.location?.lat && assignment.location?.lng
                                ? `${assignment.location.lat.toFixed(6)}, ${assignment.location.lng.toFixed(6)}`
                                : $t('assetMap.noLocationDefined')}
                            {/if}
                          {:else}
                            {$t('assetMap.noLocationDefined')}
                          {/if}
                        </span>
                      </div>
                      {#if shownAtributoIds.size > 0}
                        {#each Array.from(shownAtributoIds) as atrId (atrId)}
                          {#if assignment?.expand?.activo?.id}
                            <div class="detail-item">
                              <span class="detail-label">{(atributosList.find(a => a.id === atrId)?.descrip) || 'Atributo'}:</span>
                              <span class="detail-value">{getActivoAtributoValue(assignment.expand.activo.id, atrId) || '-'}</span>
                            </div>
                          {/if}
                        {/each}
                      {/if}
                    </div>
                    
                    <div class="assignment-actions">
                      <button 
                        class="edit-location-btn"
                        on:click|stopPropagation={() => startLocationEdit(assignment)}
                        title={$t('assetMap.editLocation')}
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        </svg>
                        {$t('assetMap.editLocation')}
                      </button>
                      
                      <button 
                        class="select-assignment-btn"
                        on:click|stopPropagation={() => {
                          selectAssignment(assignment);
                          showList = false;
                          toasts.info($t('assetMap.toasts.switchingToMap'));

                          ensureMapReady(() => {
                            try {
                              if (assignment.location) {
                                const location = typeof assignment.location === 'string'
                                  ? (() => { try { return JSON.parse(assignment.location); } catch { return null; } })()
                                  : assignment.location;
                                if (location?.lat && location?.lng) {
                                  mapInstance.setCenter({ lat: location.lat, lng: location.lng });
                                  mapInstance.setZoom(15);
                                  console.log('Map centered on assignment:', assignment.id);
                                  toasts.success($t('assetMap.toasts.centered'));
                                } else {
                                  toasts.warning($t('assetMap.toasts.noCoords'));
                                }
                              } else {
                                toasts.warning($t('assetMap.toasts.noLocation'));
                              }
                            } catch (e) {
                              console.warn('Invalid location data for assignment:', assignment.id);
                              toasts.error($t('assetMap.toasts.locationReadError'));
                            }
                          });
                        }}
                        title={$t('assetMap.viewOnMap')}
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                        </svg>
                        {$t('assetMap.viewOnMap')}
                      </button>
                    </div>
                  </div>

                  <div class="flip-face flip-back">
                    <div class="assignment-header" style="margin-bottom: 12px;">
                      <div class="assignment-title">
                        <h4>{assignment.expand?.activo?.nombre || $t('assetMap.noName')}</h4>
                        <span class="asset-code">{$t('gallery.gallery')}</span>
                      </div>
                      <span class="status-badge {statusDisplay[assignment.sta]?.class || 'status-pending'}">
                        {statusDisplay[assignment.sta]?.label || $t('assetMap.statusPending')}
                      </span>
                    </div>
                    {#if assignment?.expand?.activo?.id}
                      {#if activoGalleryLoading.has(assignment.expand.activo.id) && !(activoGalleryCache.get(assignment.expand.activo.id) || []).length}
                        <div class="loading-spinner" style="height: 120px; display: flex; align-items: center; justify-content: center;">
                          <div class="spinner"></div>
                        </div>
                      {:else}
                        {#if (activoGalleryCache.get(assignment.expand.activo.id) || []).length > 0}
                          <div class="gallery-grid">
                            {#each (activoGalleryCache.get(assignment.expand.activo.id) || []).slice(0, 9) as imgUrl (imgUrl)}
                              <div class="gallery-item">
                                <img src={imgUrl} alt="Activo" loading="lazy" on:error={(e) => { e.currentTarget.style.display = 'none'; }} />
                              </div>
                            {/each}
                          </div>
                        {:else}
                          <div class="text-blueGray-500" style="padding: 8px 0;">No hay imágenes para este activo.</div>
                        {/if}
                      {/if}
                    {:else}
                      <div class="text-blueGray-500" style="padding: 8px 0;">Activo no disponible.</div>
                    {/if}
                    <div class="text-xs text-blueGray-400" style="margin-top: 8px;">Toca para volver</div>
                  </div>
                </div>
              </div>
            {/each}
          </div>
          
          {#if filteredAssignments.length === 0}
            <div class="no-assignments">
              <p>{$t('assetMap.noResults')}</p>
            </div>
          {/if}
        </div>
      {:else}
        <!-- Map View -->
        <div bind:this={mapContainer} class="map-container" style="position: relative;"></div>
      {/if}
    </div>
  </div>
  
  <!-- Location Edit Modal -->
  {#if editingLocation}
    <div class="location-edit-modal">
      <div class="modal-overlay" on:click={cancelLocationEdit}></div>
      <div class="modal-content">
        <div class="modal-header">
          <h3>{$t('assetMap.modal.title')}</h3>
          <button class="close-btn" on:click={cancelLocationEdit}>
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="location-map-container">
            <div bind:this={locationEditMapContainer} class="location-edit-map"></div>
          </div>
          
          <div class="location-info">
            <div class="current-location">
              <p><strong>{$t('assetMap.modal.currentLocation')}:</strong></p>
              <p>Lat: {editingLocation.currentLat?.toFixed(6) || $t('assetMap.noLocationDefined')}, Lng: {editingLocation.currentLng?.toFixed(6) || $t('assetMap.noLocationDefined')}</p>
            </div>
            
            {#if editingLocation.newLat && editingLocation.newLng}
              <div class="new-location">
                <p><strong>{$t('assetMap.modal.newLocation')}:</strong></p>
                <p>Lat: {editingLocation.newLat.toFixed(6)}, Lng: {editingLocation.newLng.toFixed(6)}</p>
              </div>
            {/if}
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="cancel-btn" on:click={cancelLocationEdit}>{$t('common.cancel')}</button>
          <button class="save-btn" on:click={saveLocationChange}>{$t('common.saveChanges')}</button>
        </div>
      </div>
    </div>
  {/if}

  <!-- Asset Details Section -->
  {#if selectedAssignment}
    <div class="asset-details">
      <h3>{$t('assetMap.modal.title')}</h3>
      <div class="asset-info">
        <div class="info-item">
          <span class="info-label">{$t('assetMap.infoWindow.user')}:</span>
          <span class="info-value">{selectedAssignment.expand?.user?.name || $t('assetMap.noName')}</span>
        </div>
        <div class="info-item">
          <span class="info-label">{$t('assetMap.infoWindow.asset')}:</span>
          <span class="info-value">{selectedAssignment.expand?.activo?.nombre || $t('assetMap.noName')}</span>
        </div>
        <div class="info-item">
          <span class="info-label">{$t('assetMap.infoWindow.assignedDate')}:</span>
          <span class="info-value">{new Date(selectedAssignment.created).toLocaleDateString('es-GT')}</span>
        </div>
        <div class="info-item">
          <span class="info-label">{$t('assetMap.infoWindow.returnDate')}:</span>
          <span class="info-value">{selectedAssignment.return ? new Date(selectedAssignment.return).toLocaleDateString('es-GT') : $t('assetMap.noReturnDate')}</span>
        </div>
        <div class="info-item">
          <span class="info-label">{$t('assetMap.infoWindow.status')}:</span>
          <span class="status-badge {statusDisplay[selectedAssignment.sta]?.class || 'status-pending'}">
            {statusDisplay[selectedAssignment.sta]?.label || $t('assetMap.statusPending')}
          </span>
        </div>
      </div>
    </div>
  {/if}

  <!-- Asset Assignment Modal -->
  <AddAssetAssignmentModal 
    isOpen={isAssignmentModalOpen}
    on:assignment-created={handleAssignmentCreated}
    on:close-modal={closeAssignmentModal}
  />
</div> 